// frontend/src/components/agent/CreateAccountSection.jsx
import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import { isFieldVisible } from './permissionRules';
import ToastHost, { useToast } from '../shared/Toast';

const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };

const DEFAULT_NEW_CUSTOMER = {
  username: '', password: '', name: '',
  weeklyLimit: 100, minBet: 0.01, maxBet: 100,
  parlayMinBet: 0.01, parlayMaxBet: 100,
  liveMinBet: 0.01, liveMaxBet: 100, casinoMinBet: 0.01, casinoMaxBet: 100,
  casinoWeeklyMaxWin: 0, casinoWeeklyMaxLoss: 0,
  freePlayLossPercentage: 30, gameLimit: 0, casinoAccess: true,
};

const DEFAULT_NEW_AGENT = {
  username: '', password: '', name: '',
  weeklyLimit: 0, minBet: 0.01, maxBet: 0,
  parlayMinBet: 0.01, parlayMaxBet: 0, agentCut: 30,
};

export default function CreateAccountSection({ perms }) {
  const { toast, showToast } = useToast();
  const canCustomer = !!perms.can_add_customers;
  const canAgent = !!perms.can_add_agents;
  const [role, setRole] = useState(canCustomer ? 'customer' : 'agent');
  const [newCustomer, setNewCustomer] = useState(DEFAULT_NEW_CUSTOMER);
  const [newAgent, setNewAgent] = useState(DEFAULT_NEW_AGENT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canAgent) return;
    // Pre-fill from the book's Default Agent Template (read-only for
    // agents) instead of the hardcoded literals above, so this form shows
    // the same starting values a new sub-agent will actually be created
    // with. Only affects the form's starting values — the book's own
    // create-account fallback (server-side) is what's actually
    // authoritative, this is just a preview of it.
    api.getAgentDefaultTemplate().then(t => {
      setNewAgent(prev => ({
        ...prev,
        weeklyLimit: t.weekly_limit,
        minBet: t.min_bet,
        maxBet: t.max_bet,
        parlayMinBet: t.parlay_min_bet,
        parlayMaxBet: t.parlay_max_bet,
        agentCut: t.agent_cut,
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAgent]);

  const handleCreateCustomer = async () => {
    setSaving(true);
    try {
      const payload = { username: newCustomer.username, password: newCustomer.password, name: newCustomer.name, role: 'customer' };
      // Always send an explicit weekly_limit, even when the field is hidden
      // (no can_change_credit_limit grant) — the server defaults an omitted
      // key to $10,000, which would silently consume the agent's entire
      // allocation on the very first customer created this way. Sending 0
      // explicitly when hidden means the new customer gets no weekly limit
      // until the book or a permitted agent sets one — safe, and does not
      // eat into anyone's allocation.
      payload.weekly_limit = isFieldVisible('weekly_limit', perms) ? newCustomer.weeklyLimit : 0;
      if (isFieldVisible('min_bet', perms)) payload.min_bet = newCustomer.minBet;
      if (isFieldVisible('max_bet', perms)) payload.max_bet = newCustomer.maxBet;
      if (isFieldVisible('parlay_min_bet', perms)) payload.parlay_min_bet = newCustomer.parlayMinBet;
      if (isFieldVisible('parlay_max_bet', perms)) payload.parlay_max_bet = newCustomer.parlayMaxBet;
      if (isFieldVisible('live_min_bet', perms)) payload.live_min_bet = newCustomer.liveMinBet;
      if (isFieldVisible('live_max_bet', perms)) payload.live_max_bet = newCustomer.liveMaxBet;
      if (isFieldVisible('casino_min_bet', perms)) payload.casino_min_bet = newCustomer.casinoMinBet;
      if (isFieldVisible('casino_max_bet', perms)) payload.casino_max_bet = newCustomer.casinoMaxBet;
      if (isFieldVisible('casino_weekly_max_win', perms)) payload.casino_weekly_max_win = newCustomer.casinoWeeklyMaxWin;
      if (isFieldVisible('casino_weekly_max_loss', perms)) payload.casino_weekly_max_loss = newCustomer.casinoWeeklyMaxLoss;
      if (isFieldVisible('free_play_loss_percentage', perms)) payload.free_play_loss_percentage = newCustomer.freePlayLossPercentage;
      if (isFieldVisible('game_limit', perms)) payload.game_limit = newCustomer.gameLimit;
      if (isFieldVisible('casino_access', perms)) payload.casino_access = newCustomer.casinoAccess;

      const result = await api.createAccount(payload);
      showToast(result.message || 'Customer created');
      setNewCustomer(DEFAULT_NEW_CUSTOMER);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create account', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAgent = async () => {
    setSaving(true);
    try {
      const result = await api.createAccount({
        username: newAgent.username, password: newAgent.password, name: newAgent.name, role: 'agent',
        weekly_limit: newAgent.weeklyLimit, min_bet: newAgent.minBet, max_bet: newAgent.maxBet,
        parlay_min_bet: newAgent.parlayMinBet, parlay_max_bet: newAgent.parlayMaxBet,
        agent_cut: newAgent.agentCut,
      });
      showToast(result.message || 'Agent created');
      setNewAgent(DEFAULT_NEW_AGENT);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create account', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <ToastHost toast={toast} />
      <h2 style={{ color: colors.textPrimary, marginBottom: '1rem' }}>Create Account</h2>
      <div style={{ background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, padding: '1.25rem' }}>
        {canCustomer && canAgent && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Role</label>
            <select style={{ ...inputStyle, backgroundColor: '#1a0822' }} value={role} onChange={e => setRole(e.target.value)}>
              <option style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value="customer">Customer</option>
              <option style={{ backgroundColor: '#1a0822', color: colors.textPrimary }} value="agent">Agent</option>
            </select>
          </div>
        )}

        {role === 'customer' && canCustomer && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} value={newCustomer.username} onChange={e => setNewCustomer({ ...newCustomer, username: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" style={inputStyle} value={newCustomer.password} onChange={e => setNewCustomer({ ...newCustomer, password: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input style={inputStyle} value={newCustomer.name} onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} />
            </div>
            {isFieldVisible('weekly_limit', perms) && (
              <div>
                <label style={labelStyle}>Weekly Limit</label>
                <input type="number" style={inputStyle} value={newCustomer.weeklyLimit} onChange={e => setNewCustomer({ ...newCustomer, weeklyLimit: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('min_bet', perms) && (
              <div>
                <label style={labelStyle}>Min Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.minBet} onChange={e => setNewCustomer({ ...newCustomer, minBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('max_bet', perms) && (
              <div>
                <label style={labelStyle}>Max Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.maxBet} onChange={e => setNewCustomer({ ...newCustomer, maxBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('parlay_min_bet', perms) && (
              <div>
                <label style={labelStyle}>Parlay Min Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.parlayMinBet} onChange={e => setNewCustomer({ ...newCustomer, parlayMinBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('parlay_max_bet', perms) && (
              <div>
                <label style={labelStyle}>Parlay Max Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.parlayMaxBet} onChange={e => setNewCustomer({ ...newCustomer, parlayMaxBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('live_min_bet', perms) && (
              <div>
                <label style={labelStyle}>Live Min Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.liveMinBet} onChange={e => setNewCustomer({ ...newCustomer, liveMinBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('live_max_bet', perms) && (
              <div>
                <label style={labelStyle}>Live Max Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.liveMaxBet} onChange={e => setNewCustomer({ ...newCustomer, liveMaxBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('casino_min_bet', perms) && (
              <div>
                <label style={labelStyle}>Casino Min Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.casinoMinBet} onChange={e => setNewCustomer({ ...newCustomer, casinoMinBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('casino_max_bet', perms) && (
              <div>
                <label style={labelStyle}>Casino Max Bet</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.casinoMaxBet} onChange={e => setNewCustomer({ ...newCustomer, casinoMaxBet: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('casino_weekly_max_win', perms) && (
              <div>
                <label style={labelStyle}>Casino Weekly Max Win</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.casinoWeeklyMaxWin} onChange={e => setNewCustomer({ ...newCustomer, casinoWeeklyMaxWin: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('casino_weekly_max_loss', perms) && (
              <div>
                <label style={labelStyle}>Casino Weekly Max Loss</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.casinoWeeklyMaxLoss} onChange={e => setNewCustomer({ ...newCustomer, casinoWeeklyMaxLoss: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('free_play_loss_percentage', perms) && (
              <div>
                <label style={labelStyle}>Free Play Loss Rebate (%)</label>
                <input type="number" step="1" min="0" max="100" style={inputStyle} value={newCustomer.freePlayLossPercentage} onChange={e => setNewCustomer({ ...newCustomer, freePlayLossPercentage: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('game_limit', perms) && (
              <div>
                <label style={labelStyle}>Contest Limit</label>
                <input type="number" step="0.01" style={inputStyle} value={newCustomer.gameLimit} onChange={e => setNewCustomer({ ...newCustomer, gameLimit: Number(e.target.value) })} />
              </div>
            )}
            {isFieldVisible('casino_access', perms) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingTop: '1.4rem' }}>
                <input type="checkbox" checked={newCustomer.casinoAccess} onChange={e => setNewCustomer({ ...newCustomer, casinoAccess: e.target.checked })} />
                <label style={{ color: colors.textPrimary, fontSize: '0.9rem' }}>Casino Access</label>
              </div>
            )}
          </div>
        )}

        {role === 'agent' && canAgent && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Username</label>
              <input style={inputStyle} value={newAgent.username} onChange={e => setNewAgent({ ...newAgent, username: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" style={inputStyle} value={newAgent.password} onChange={e => setNewAgent({ ...newAgent, password: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input style={inputStyle} value={newAgent.name} onChange={e => setNewAgent({ ...newAgent, name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Weekly Limit</label>
              <input type="number" style={inputStyle} value={newAgent.weeklyLimit} onChange={e => setNewAgent({ ...newAgent, weeklyLimit: Number(e.target.value) })} />
            </div>
            <div>
              <label style={labelStyle}>Min Bet</label>
              <input type="number" step="0.01" style={inputStyle} value={newAgent.minBet} onChange={e => setNewAgent({ ...newAgent, minBet: Number(e.target.value) })} />
            </div>
            <div>
              <label style={labelStyle}>Max Bet</label>
              <input type="number" step="0.01" style={inputStyle} value={newAgent.maxBet} onChange={e => setNewAgent({ ...newAgent, maxBet: Number(e.target.value) })} />
            </div>
            <div>
              <label style={labelStyle}>Parlay Min Bet</label>
              <input type="number" step="0.01" style={inputStyle} value={newAgent.parlayMinBet} onChange={e => setNewAgent({ ...newAgent, parlayMinBet: Number(e.target.value) })} />
            </div>
            <div>
              <label style={labelStyle}>Parlay Max Bet</label>
              <input type="number" step="0.01" style={inputStyle} value={newAgent.parlayMaxBet} onChange={e => setNewAgent({ ...newAgent, parlayMaxBet: Number(e.target.value) })} />
            </div>
            <div>
              <label style={labelStyle}>Agent Cut (%)</label>
              <input type="number" min="0" max="100" step="0.1" style={inputStyle} value={newAgent.agentCut} onChange={e => setNewAgent({ ...newAgent, agentCut: Number(e.target.value) })} />
            </div>
          </div>
        )}

        <button
          onClick={role === 'customer' ? handleCreateCustomer : handleCreateAgent}
          disabled={saving}
          style={{ marginTop: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily }}
        >
          {saving ? 'Creating...' : 'Create Account'}
        </button>
      </div>
    </div>
  );
}
